import Icon from '@mui/material/Icon';
import React, { useMemo } from 'react';
import { isEqualsIgnoringCase, toBoolean } from 'utils/stringUtils';
import { useIntl } from 'react-intl';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import app from 'config/app.json';
import _ from 'lodash';
import { PositionType } from 'hook/useConf';
import Tasks from 'pages/tasks/Tasks';
import ContainerWithEntityDetails from 'pages/entities/details/ContainerWithEntityDetails';
import SuspenseContainer from 'components/Loading/SuspenseContainer';
import { useAuth } from 'hook/useAuth';
import { useQueryData } from 'hook/useQueryData';
import { useGetCurrentApplicationQuery } from 'services/applicationApi';

const LoadableDefaultTimeline = React.lazy(() => import('pages/entities/timeline/DefaultTimeline'));
const LoadableDefaultListing = React.lazy(() => import('pages/entities/listing/DefaultListing'));
const LoadableAdvancedListing = React.lazy(() => import('pages/entities/listing/AdvancedListing'));
const LoadableDefaultEntityDetails = React.lazy(() =>
  import('pages/entities/details/DefaultEntityDetails')
);
const LoadableDashBoard = React.lazy(() => import('pages/dashboard'));
const LoadableEntitySearch = React.lazy(() => import('pages/entities/search/EntitySearch'));
const LoadableGlobalSearch = React.lazy(() => import('pages/globalSearch/GlobalSearch'));
const LoadableFileViewer = React.lazy(() => import('pages/fileViewer/FileViewerContainer'));

const useRoutes = (views, conf) => {
  const { formatMessage: msg } = useIntl();

  const { currentUser } = useAuth();
  const { data: currentApplication } = useQueryData(useGetCurrentApplicationQuery);

  return useMemo(() => {
    let res = views
      .map((view, index) => {
        if (toBoolean(conf(`${view}.enabled`), true)) {
          const viewType = conf(`${view}.type`);

          let component = null;
          let pathSuffix = '';
          let standalone = { enabled: false };
          if (viewType === 'dashboard') {
            standalone = { enabled: true };
            component = <LoadableDashBoard />;
          }
          if (viewType === 'timeline') {
            standalone = { enabled: true };
            component = <LoadableDefaultTimeline />;
          } else if (viewType === 'listing') {
            standalone = { enabled: true };
            component = <LoadableDefaultListing />;
          } else if (viewType === 'advancedListing') {
            standalone = { enabled: true };
            component = <LoadableAdvancedListing />;
          } else if (viewType === 'preview') {
            component = <LoadableDefaultEntityDetails />;
            pathSuffix = '/:entityId';
          } else if (viewType === 'viewer') {
            component = <LoadableFileViewer />;
            pathSuffix = '/:hash?';
            standalone = { enabled: true, navbarHidden: true };
          } else if (viewType === 'standardSearch') {
            component = (
              <SuspenseContainer>
                {conf(`${view}.result.detailedView`) ? (
                  <ContainerWithEntityDetails
                    view={view}
                    selectedStateSelector={(state) => state.search.selectedEntityId}
                    resultsStateSelector={(state) => state.search.searchResults}
                  >
                    <LoadableEntitySearch />
                  </ContainerWithEntityDetails>
                ) : (
                  <LoadableEntitySearch />
                )}
              </SuspenseContainer>
            );
            standalone = { enabled: true };
          }
          if (component) {
            component = <SuspenseContainer>{component}</SuspenseContainer>;
            return {
              visibleInMenu: toBoolean(conf(`${view}.visibleInMenu`, true)),
              level: Number(conf(`${view}.level`, 100000 + index)),
              type: 'collapse',
              name: msg({ id: `view.${view}.shortTitle` }),
              key: view,
              icon: <Icon fontSize="small">{msg({ id: `view.${view}.icon` })}</Icon>,
              viewType,
              view: `${view}`,
              path: `${viewType}/:view${pathSuffix}`,
              standalone,
              component
            };
          }
        }
        return null;
      })
      .filter((view) => view != null);
    // Adds the old defintion of static search view (prevents from migrating .json files)
    if (conf(app.view.search) && toBoolean(conf(`${app.view.search}.enabled`), true)) {
      res = res.concat([
        {
          type: 'collapse',
          name: 'Standard Search',
          key: 'standardSearch',
          level: Number(conf(`${app.view.search}.level`, 100000 + res.length)),
          icon: <Icon fontSize="small">manage_search_rounded</Icon>,
          viewType: 'standardSearch',
          view: `${app.view.search}`,
          path: `standardSearch/:view`,
          standalone: { enabled: true },
          component: (
            <SuspenseContainer>
              {conf(`${app.view.search}.result.detailedView`) ? (
                <ContainerWithEntityDetails
                  view={app.view.search}
                  selectedStateSelector={(state) => state.search.selectedEntityId}
                  resultsStateSelector={(state) => state.search.searchResults}
                >
                  <LoadableEntitySearch />
                </ContainerWithEntityDetails>
              ) : (
                <LoadableEntitySearch />
              )}
            </SuspenseContainer>
          )
        }
      ]);
    }
    // Sort views by level
    res = _.sortBy(res, ['level']);
    // Adds views not visible in menu
    if (toBoolean(conf(`${app.view.globalSearch}.enabled`), true)) {
      res = res.concat([
        {
          type: 'collapse',
          visibleInMenu: false,
          name: 'Global Search',
          key: 'globalSearch',
          icon: <TravelExploreRoundedIcon fontSize="small" />,
          viewType: 'globalSearch',
          path: 'globalSearch',
          standalone: { enabled: true },
          component: (
            <SuspenseContainer>
              {conf(`globalSearch.result.detailedView`) ? (
                <ContainerWithEntityDetails
                  view={app.view.globalSearch}
                  selectedStateSelector={(state) => state.search.selectedEntityId}
                  resultsStateSelector={(state) => state.search.searchResults}
                >
                  <LoadableGlobalSearch />
                </ContainerWithEntityDetails>
              ) : (
                <LoadableGlobalSearch />
              )}
            </SuspenseContainer>
          )
        }
        // {
        //   type: 'divider',
        //   key: 'divider'
        // },
        // {
        //   type: 'title',
        //   name: 'Demo',
        //   key: 'demo',
        //   title: 'Démo'
        // },
        // {
        //   type: 'collapse',
        //   name: 'Profile',
        //   key: 'profile',
        //   icon: <Icon fontSize="small">person</Icon>,
        //   path: 'profile',
        //   component: <Profile />
        // },
        // {
        //   type: 'collapse',
        //   name: 'Sign Up',
        //   key: 'sign-up',
        //   icon: <Icon fontSize="small">assignment</Icon>,
        //   path: '/authentication/sign-up',
        //   component: <SignUp />
        // }
      ]);
    }

    let numberOfTopElements = 0;
    let numberOfBottomElements = 0;
    // Adds the route for "tasks" if configured
    const tasksConf = conf(`tasks`);
    const someTasksEnabled = tasksConf?.content?.some((taskConf) =>
      toBoolean(taskConf.enabled, true)
    );
    const appSecured = currentApplication?.secured;
    if (
      tasksConf?.enabled &&
      ((!appSecured && someTasksEnabled) ||
        (appSecured && !currentUser.user) ||
        (appSecured && currentUser.user && someTasksEnabled))
    ) {
      if (isEqualsIgnoringCase(tasksConf.positionInMenu, PositionType.Bottom)) {
        numberOfBottomElements += 1;
      } else {
        numberOfTopElements += 1;
      }

      res.splice(
        isEqualsIgnoringCase(tasksConf.positionInMenu, PositionType.Bottom) ? res.length : 0,
        0,
        {
          type: 'collapse',
          viewType: 'tasks',
          name: msg({ id: `view.tasks.shortTitle` }),
          key: 'tasks',
          path: 'tasks',
          icon: <Icon fontSize="small">{msg({ id: `view.tasks.icon` })}</Icon>,
          component: (
            <SuspenseContainer>
              <Tasks />
            </SuspenseContainer>
          )
        }
      );
    }

    if (numberOfTopElements > 0) {
      res.splice(numberOfTopElements, 0, {
        type: 'divider',
        key: 'divider'
      });
    }
    if (numberOfBottomElements > 0) {
      res.splice(res.length - 1 - numberOfBottomElements, 0, {
        type: 'divider',
        key: 'divider'
      });
    }
    return res;
  }, [conf, currentApplication?.secured, currentUser.user, msg, views]);
};

export default useRoutes;
